import React, { useMemo, useEffect, useRef, useState } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import AvailabilityUtils from "../../../../utils/availability/AvailabilityUtils";
import { Button } from "../../../../shadcn/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../../shadcn/ui/card";
import { Label } from "../../../../shadcn/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../shadcn/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../../../../shadcn/ui/popover";
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from "../../../../shadcn/ui/command";
import { Switch } from "../../../../shadcn/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../../../shadcn/ui/tooltip";
import { HelpCircle, Clock, Settings, Search, Check } from "lucide-react";
import TimeSelect from "./TimeSelect";

const DayRow = React.memo(
  ({
    dayKey,
    dayLabel,
    control,
    isEnabled,
    isRowDisabled,
    isTimeDisabled,
    displayStartTime,
    displayEndTime,
    hasError,
  }) => (
    <div className="grid grid-cols-1 md:grid-cols-[auto_2fr_2fr] gap-2 md:gap-4 items-center">
      <div className="flex items-center space-x-2">
        <Controller
          name={`availability.${dayKey}.enabled`}
          control={control}
          render={({ field }) => (
            <Switch
              id={`avail-${dayKey}`}
              checked={field.value}
              onCheckedChange={field.onChange}
              disabled={isRowDisabled}
            />
          )}
        />
        <Label htmlFor={`avail-${dayKey}`} className="w-20 font-normal">
          {dayLabel}
        </Label>
      </div>

      <Controller
        name={`availability.${dayKey}.startTime`}
        control={control}
        render={({ field }) => (
          <TimeSelect
            disabled={!isEnabled || isTimeDisabled}
            value={field.value || displayStartTime}
            onChange={field.onChange}
            placeholder="Start time"
            hasError={hasError}
          />
        )}
      />

      <Controller
        name={`availability.${dayKey}.endTime`}
        control={control}
        render={({ field }) => (
          <TimeSelect
            disabled={!isEnabled || isTimeDisabled}
            value={field.value || displayEndTime}
            onChange={field.onChange}
            placeholder="End time"
            hasError={hasError}
          />
        )}
      />
    </div>
  )
);

const DAYS = [
  { key: "monday", label: "Monday" },
  { key: "tuesday", label: "Tuesday" },
  { key: "wednesday", label: "Wednesday" },
  { key: "thursday", label: "Thursday" },
  { key: "friday", label: "Friday" },
  { key: "saturday", label: "Saturday" },
  { key: "sunday", label: "Sunday" },
];

const DAY_PATTERNS = {
  all: { label: "All days", days: DAYS.map(d => d.key) },
  weekdays: { label: "Mon-Fri", days: DAYS.slice(0, 5).map(d => d.key) },
  weekends: { label: "Sat & Sun", days: DAYS.slice(5).map(d => d.key) },
  monThu: { label: "Mon-Thu", days: DAYS.slice(0, 4).map(d => d.key) },
  friSun: { label: "Fri-Sun", days: DAYS.slice(4).map(d => d.key) },
  monSat: { label: "Mon-Sat", days: DAYS.slice(0, 6).map(d => d.key) },
};

const TIME_PRESETS = [
  // General
  { key: "fullDay", label: "Full Day", pattern: "all", startTime: "00:00", endTime: "23:59" },
  {
    key: "fullDayWeekends",
    label: "Weekends Only - Full Day",
    pattern: "weekends",
    startTime: "00:00",
    endTime: "23:59",
  },
  {
    key: "fullDayWeekdays",
    label: "Weekdays Only - Full Day",
    pattern: "weekdays",
    startTime: "00:00",
    endTime: "23:59",
  },
  {
    key: "fullDayMonThu",
    label: "4-Day Week (Mon-Thu) - Full Day",
    pattern: "monThu",
    startTime: "00:00",
    endTime: "23:59",
  },
  { key: "fullDayFriSun", label: "Fri-Sun - Full Day", pattern: "friSun", startTime: "00:00", endTime: "23:59" },
  // Work
  { key: "office", label: "Office 9-5", pattern: "all", startTime: "09:00", endTime: "17:00" },
  { key: "officeWeekdays", label: "Weekdays Only - 9-5", pattern: "weekdays", startTime: "09:00", endTime: "17:00" },
  { key: "officeWeekends", label: "Weekends Only - 9-5", pattern: "weekends", startTime: "09:00", endTime: "17:00" },
  { key: "officeMonThu", label: "4-Day Week (Mon-Thu) - 9-5", pattern: "monThu", startTime: "09:00", endTime: "17:00" },
  { key: "extendedWork", label: "Extended Work (8am-8pm)", pattern: "all", startTime: "08:00", endTime: "20:00" },
  // Stay
  { key: "hotel", label: "Hotel (12pm check-in)", pattern: "all", startTime: "12:00", endTime: "23:59" },
  { key: "hotelWeekends", label: "Weekend Hotel (12pm in)", pattern: "weekends", startTime: "12:00", endTime: "23:59" },
  { key: "lodge", label: "Lodge (2pm check-in)", pattern: "all", startTime: "14:00", endTime: "23:59" },
  // Play
  { key: "dayOut", label: "Day Out (10am-10pm)", pattern: "all", startTime: "10:00", endTime: "22:00" },
  {
    key: "dayOutWeekends",
    label: "Weekend Day Out (10am-10pm)",
    pattern: "weekends",
    startTime: "10:00",
    endTime: "22:00",
  },
  { key: "evening", label: "Evening Out (6pm-11pm)", pattern: "all", startTime: "18:00", endTime: "23:00" },
  // Move
  { key: "travelHours", label: "Travel Hours (5am-11pm)", pattern: "all", startTime: "05:00", endTime: "23:00" },
  { key: "rushHour", label: "Rush Hours (6am-9pm)", pattern: "all", startTime: "06:00", endTime: "21:00" },
  // Food
  { key: "breakfast", label: "Breakfast (7am-11am)", pattern: "all", startTime: "07:00", endTime: "11:00" },
  { key: "lunch", label: "Lunch (11am-3pm)", pattern: "all", startTime: "11:00", endTime: "15:00" },
  { key: "dinner", label: "Dinner (5pm-11pm)", pattern: "all", startTime: "17:00", endTime: "23:00" },
  { key: "allDayDine", label: "All-Day Dine (7am-11pm)", pattern: "all", startTime: "07:00", endTime: "23:00" },
  {
    key: "weekendBrunch",
    label: "Weekend Brunch (9am-2pm)",
    pattern: "weekends",
    startTime: "09:00",
    endTime: "14:00",
  },
];

// Most common presets shown as quick buttons; the rest live in the searchable "More presets" picker
const QUICK_PRESETS = TIME_PRESETS.filter(p => ["fullDay", "office", "hotel"].includes(p.key));

// Everything visible in the list is searchable: name, day pattern, and time range
const getPresetSearchText = preset => {
  const daysLabel = DAY_PATTERNS[preset.pattern]?.label || "All days";
  return `${preset.label} ${daysLabel} ${preset.startTime} ${preset.endTime}`.toLowerCase();
};

const FieldTip = ({ children }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <HelpCircle className="w-4 h-4 ml-1 text-gray-400 cursor-help" />
    </TooltipTrigger>
    <TooltipContent side="top" className="max-w-xs">
      {children}
    </TooltipContent>
  </Tooltip>
);

const GlobalSettingsCard = ({ control, errors, isReadOnly = false, originalAvailability }) => {
  const { setValue, getValues } = useFormContext();
  const bookingDuration = useWatch({ control, name: "bookingDuration", defaultValue: "hourly" });
  const category = useWatch({ control, name: "category", defaultValue: "WORK" });
  const availability = useWatch({ control, name: "availability" });

  const isRowDisabled = isReadOnly;
  const isTimeDisabled = isReadOnly;

  const { displayStartTime, displayEndTime } = useMemo(() => {
    const normalizedCategory = AvailabilityUtils.getServiceCategory(category);
    const constraints = AvailabilityUtils.getTimeConstraints(normalizedCategory, bookingDuration?.toUpperCase());
    return {
      displayStartTime: constraints.startTime,
      displayEndTime: constraints.endTime,
    };
  }, [category, bookingDuration]);

  const hasError = !!errors?.availability?.message;
  const [appliedPreset, setAppliedPreset] = useState(null);
  const [presetOpen, setPresetOpen] = useState(false);
  const [applyingPreset, setApplyingPreset] = useState(false);
  const presetTimerRef = useRef(null);

  useEffect(() => {
    return () => clearTimeout(presetTimerRef.current);
  }, []);

  // Apply times to the given days; presets also enable the pattern's days and
  // disable every other day.
  // shouldValidate is deliberately skipped here: full-form validation on this
  // large form caused a visible delay when applying a preset.
  const applyTimes = (days, startTime, endTime, enableDays = false) => {
    const nextAvailability = {};
    DAYS.forEach(({ key }) => {
      const current = getValues(`availability.${key}`) || {};
      const inPattern = days.includes(key);
      if (!enableDays) {
        nextAvailability[key] = inPattern ? { ...current, startTime, endTime } : current;
      } else {
        nextAvailability[key] = inPattern
          ? { ...current, enabled: true, startTime, endTime }
          : { ...current, enabled: false };
      }
    });
    setValue("availability", nextAvailability, { shouldDirty: true, shouldTouch: true });
  };

  const handlePreset = preset => {
    if (!preset) return;
    const pattern = DAY_PATTERNS[preset.pattern] || DAY_PATTERNS.all;
    applyTimes(pattern.days, preset.startTime, preset.endTime, true);
    setAppliedPreset(preset);
    setPresetOpen(false);
    setApplyingPreset(true);
    clearTimeout(presetTimerRef.current);
    presetTimerRef.current = setTimeout(() => setApplyingPreset(false), 1000);
  };

  // Restore to the category default times
  const handleDefault = () => {
    applyTimes(DAY_PATTERNS.all.days, displayStartTime, displayEndTime);
    setAppliedPreset(null);
  };

  // Clear user changes and go back to what the last fetch returned
  const handleClear = () => {
    const original = originalAvailability ? JSON.parse(JSON.stringify(originalAvailability)) : {};
    setValue("availability", original, { shouldDirty: true, shouldTouch: true });
    setAppliedPreset(null);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="text-xl flex items-center">
              <Settings className="w-5 h-5 mr-2" />
              Availability & Sync
            </CardTitle>
            <CardDescription className="text-sm text-gray-500 mt-1">
              When this space can be booked and global settings for all units
            </CardDescription>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        <TooltipProvider>
          {/* Space Availability Section */}
          <div>
            <Label className="flex items-center mb-3">
              <Clock className="w-4 h-4 mr-1" />
              Weekly Schedule{" "}
              <FieldTip>
                Define operating hours for each day. Customers can only book within these time windows.
              </FieldTip>
            </Label>
            <p className="text-xs text-gray-500 mb-3">
              Set when this space is available for booking (applies to all units)
            </p>
            <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
              {/* Label */}
              <span className="flex shrink-0 items-center gap-1.5 text-xs font-medium text-slate-600">
                <Clock className="h-3.5 w-3.5 text-blue-500" />
                Quick set times
              </span>
              <span className="h-4 w-px bg-slate-200 shrink-0" />
              {/* Preset chips */}
              {QUICK_PRESETS.map(preset => {
                const isActive = appliedPreset?.key === preset.key;
                return (
                  <button
                    key={preset.key}
                    type="button"
                    disabled={isReadOnly}
                    onClick={() => handlePreset(preset)}
                    className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 disabled:pointer-events-none disabled:opacity-40 ${
                      isActive
                        ? "border-blue-500 bg-blue-600 text-white shadow-sm"
                        : "border-slate-200 bg-white text-slate-600 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700"
                    }`}
                  >
                    {isActive && <Check className="h-3 w-3 shrink-0" />}
                    {preset.label}
                  </button>
                );
              })}
              <span className="h-4 w-px bg-slate-200 shrink-0" />
              {/* Utility actions */}
              <button
                type="button"
                disabled={isReadOnly}
                onClick={handleDefault}
                className="text-xs text-slate-500 hover:text-slate-700 disabled:opacity-40 disabled:pointer-events-none"
              >
                Default
              </button>
              <button
                type="button"
                disabled={isReadOnly}
                onClick={handleClear}
                className="text-xs text-red-400 hover:text-red-600 disabled:opacity-40 disabled:pointer-events-none"
              >
                Clear
              </button>
              {/* More presets popover */}
              <Popover open={presetOpen} onOpenChange={setPresetOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    disabled={isReadOnly}
                    className="ml-auto inline-flex items-center gap-1 text-xs text-slate-500 hover:text-blue-600 disabled:opacity-40 disabled:pointer-events-none"
                  >
                    <Search className="h-3 w-3" />
                    More presets
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-[320px] p-0 overflow-hidden rounded-xl shadow-xl border-slate-200" align="end">
                  <Command filter={(value, search) => (value.includes(search.toLowerCase()) ? 1 : 0)}>
                    <CommandInput placeholder="e.g. weekend, brunch, 17:00..." />
                    <CommandList className="p-1.5">
                      <CommandEmpty className="py-8 text-sm text-muted-foreground">
                        No preset matches your search
                      </CommandEmpty>
                      {TIME_PRESETS.map(preset => (
                        <CommandItem
                          key={preset.key}
                          value={getPresetSearchText(preset)}
                          onSelect={() => handlePreset(preset)}
                          className="flex items-center justify-between gap-2 rounded-md px-2.5 py-2 cursor-pointer"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            {appliedPreset?.key === preset.key && (
                              <Check className="h-4 w-4 shrink-0 text-emerald-600" />
                            )}
                            <div className="flex flex-col min-w-0">
                              <span className="text-sm font-medium leading-tight truncate">{preset.label}</span>
                              <span className="text-xs text-muted-foreground leading-tight">
                                {DAY_PATTERNS[preset.pattern]?.label}
                              </span>
                            </div>
                          </div>
                          <span className="shrink-0 rounded-md bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-600 tabular-nums">
                            {preset.startTime}-{preset.endTime}
                          </span>
                        </CommandItem>
                      ))}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            {applyingPreset ? (
              <div className="space-y-3">
                {DAYS.map(({ key }) => (
                  <div
                    key={key}
                    className="grid grid-cols-1 md:grid-cols-[auto_2fr_2fr] gap-2 md:gap-4 items-center animate-pulse"
                  >
                    <div className="flex items-center space-x-2">
                      <div className="w-9 h-5 rounded-full bg-slate-200" />
                      <div className="w-20 h-4 rounded bg-slate-200" />
                    </div>
                    <div className="h-10 rounded-md bg-slate-200" />
                    <div className="h-10 rounded-md bg-slate-200" />
                  </div>
                ))}
              </div>
            ) : (
              <div className={`space-y-3 ${isRowDisabled ? "opacity-50 pointer-events-none" : ""}`}>
                {DAYS.map(({ key, label }) => (
                  <DayRow
                    key={key}
                    dayKey={key}
                    dayLabel={label}
                    control={control}
                    isEnabled={availability?.[key]?.enabled || false}
                    isRowDisabled={isRowDisabled}
                    isTimeDisabled={isTimeDisabled}
                    displayStartTime={displayStartTime}
                    displayEndTime={displayEndTime}
                    hasError={
                      hasError || !!errors?.availability?.[key]?.startTime || !!errors?.availability?.[key]?.endTime
                    }
                  />
                ))}
              </div>
            )}
            {errors.availability && <p className="text-sm text-red-500 mt-2">{errors.availability.message}</p>}
          </div>

          {/* Divider */}
          <div className="border-t border-gray-200"></div>

          {/* Buffer Time */}
          <div>
            <Label htmlFor="buffer-time" className="flex items-center mb-1">
              Buffer Time{" "}
              <FieldTip>The gap between consecutive bookings for cleaning, preparation, or turnover.</FieldTip>
            </Label>
            <p className="text-xs text-gray-500 mb-2">Time between bookings for cleaning and setup</p>
            <Controller
              name="bufferTime"
              control={control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange} disabled={isReadOnly}>
                  <SelectTrigger id="buffer-time">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">No buffer</SelectItem>
                    <SelectItem value="15">15 Minutes</SelectItem>
                    <SelectItem value="30">30 Minutes</SelectItem>
                    <SelectItem value="60">1 Hour</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
            {errors.bufferTime && <p className="text-sm text-red-500 mt-1">{errors.bufferTime.message}</p>}
          </div>

          {/* Smart Lock */}
          <div>
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="smart-lock" className="flex items-center">
                  Enable Smart Lock Access Control{" "}
                  <FieldTip>
                    Coming soon — grant guests keyless entry via smart locks, activated automatically on booking
                    confirmation.
                  </FieldTip>
                </Label>
                <p className="text-sm text-gray-500">Grant keyless entry for confirmed bookings</p>
              </div>
              <Switch id="smart-lock" disabled={true} />
            </div>
          </div>
        </TooltipProvider>
      </CardContent>
    </Card>
  );
};

export default GlobalSettingsCard;
